<template>
    <div class="vfb-choice-group vfb-choice-group--radio" :class="classes.wrapper">
        <div class="vfb-choice-group__container" :class="classes.container">
            <fieldset class="vfb-fieldset" :class="classes.fieldset">
                <legend v-if="label" class="vfb-legend" :class="classes.legend">
                    {{ label }}
                    <span v-if="required" class="text-danger">*</span>
                </legend>

                <div class="vfb-choice-group__options" :class="[classes.group, `${classes.group}--${layout}`]"
                     :aria-describedby="helpText ? id + '-help-block' : undefined">
                    <div v-for="option in options" :key="option.value" class="vfb-choice-group__item" :class="classes.item">
                        <input
                            class="vfb-check-input"
                            :id="`${id}-${option.value}`"
                            :name="name"
                            v-model="model"
                            :value="option.value"
                            type="radio"
                            :class="classes.input"
                            :disabled="disabled || option.disabled"
                            :required="required"
                        />
                        <label
                            :for="`${id}-${option.value}`"
                            class="vfb-option-label"
                            :class="classes.itemLabel"
                        >
                            {{ option.label }}
                        </label>
                    </div>
                </div>

                <HelpText
                    v-if="helpText && helpText.length > 0"
                    :id="id + '-help-block'"
                    :helpText="helpText"
                    :className="classes.help"
                />
                <FormErrors class="vfb-errors" :class="classes.errors" />
            </fieldset>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import FormErrors from './Errors.ce.vue';
import HelpText from './includes/HelpText.ce.vue';

interface Classes {
    wrapper?: string;
    container?: string;
    fieldset?: string;
    legend?: string;
    group?: string;
    item?: string;
    input?: string;
    itemLabel?: string;
    help?: string;
    errors?: string;
}

interface Option {
    label: string;
    value: string;
    disabled?: boolean;
}

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        required: true,
    },
    modelValue: {
        type: String,
        default: '',
    },
    name: {
        type: String,
        required: true,
    },
    options: {
        type: Array as () => Option[],
        default: () => [],
    },
    layout: {
        type: String as () => 'horizontal' | 'vertical',
        default: 'vertical',
    },
    helpText: {
        type: String,
        default: '',
    },
    classes: {
        type: Object as () => Classes,
        default: () => ({
            wrapper: 'js-form-validation__group',
            container: 'position-relative',
            fieldset: 'form-group',
            legend: 'form-label',
            group: 'radio-group',
            item: 'form-check',
            input: 'form-check-input',
            itemLabel: 'form-check-label',
            help: 'form-text text-muted',
            errors: 'form-errors js-form-validation__errors invalid-feedback',
        }),
    },
});

const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
}>();

const model = computed({
    get() {
        return props.modelValue;
    },
    set(value: string) {
        emit('update:modelValue', value);
    },
});


</script>

<style lang="scss" scoped>
.radio-group--vertical {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.radio-group--horizontal {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
}

legend {
    font-size: inherit;
    float: none;
}
</style>